import { useEffect, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ALL_ROLE_IDS, ROLE_LABELS, DEPARTMENTS, USER_STATUSES,
} from '@/lib/roles';
import { UserPlus, Save, Copy } from 'lucide-react';

const NONE_VALUE = '__none__';

/** Add / edit / deactivate teammates from the Team Dashboard.
 *  `mode = 'create' | 'edit'`. When mode='edit', pass `user` to prefill. */
export function TeamMemberDialog({ open, onOpenChange, mode = 'create', user = null,
                                   users = [], onSaved }) {
  const [form, setForm] = useState(makeEmpty());
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const isEdit = mode === 'edit';

  useEffect(() => {
    if (!open) { setTempPw(null); return; }
    if (isEdit && user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'team',
        department: user.department || '',
        reports_to: user.reports_to || '',
        phone: user.phone || '',
        status: user.status || 'active',
        password: '',
      });
    } else {
      setForm(makeEmpty());
    }
  }, [open, isEdit, user]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required.'); return; }
    if (!isEdit && !form.email.trim()) { toast.error('Email is required.'); return; }
    setBusy(true);
    try {
      if (isEdit) {
        const patch = {
          name: form.name.trim(),
          role: form.role,
          department: form.department || '',
          reports_to: form.reports_to || null,
          phone: form.phone || '',
          status: form.status,
        };
        await api.patch(`/users/${user.id}`, patch);
        toast.success('Teammate updated.');
        onOpenChange(false);
        onSaved && onSaved();
      } else {
        const body = {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          role: form.role,
          department: form.department || null,
          reports_to: form.reports_to || null,
          phone: form.phone || null,
          status: form.status,
          ...(form.password ? { password: form.password } : {}),
        };
        const r = await api.post('/users', body);
        if (r.data?.initial_password) {
          setTempPw(r.data.initial_password);
          toast.success('Teammate created. Share the temporary password below.');
        } else {
          toast.success('Teammate created.');
          onOpenChange(false);
        }
        onSaved && onSaved();
      }
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };

  const managerCandidates = (users || []).filter(u => u.id !== user?.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px]" data-testid={`team-dialog-${mode}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={16} className="text-[hsl(var(--primary))]" />
            {isEdit ? `Edit ${user?.name || 'teammate'}` : 'Add a teammate'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the details below. Changes take effect immediately.'
              : 'Create a new account. Email domain must match the studio domain; the temp password is shown once.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" required>
            <Input value={form.name}
                   onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                   data-testid="tm-name" placeholder="Full name" />
          </Field>
          <Field label="Email" required>
            <Input value={form.email}
                   onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                   data-testid="tm-email" placeholder="name@32december.design"
                   disabled={isEdit} />
          </Field>
          <Field label="Role" required>
            <Select value={form.role}
                    onValueChange={v => setForm(f => ({ ...f, role: v }))}>
              <SelectTrigger data-testid="tm-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ALL_ROLE_IDS.map(r => (
                  <SelectItem key={r} value={r} data-testid={`tm-role-opt-${r}`}>
                    {ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Department">
            <Select value={form.department || NONE_VALUE}
                    onValueChange={v => setForm(f => ({ ...f, department: v === NONE_VALUE ? '' : v }))}>
              <SelectTrigger data-testid="tm-department"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>— None —</SelectItem>
                {DEPARTMENTS.map(d => (
                  <SelectItem key={d} value={d} data-testid={`tm-dept-opt-${d}`}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reports to">
            <Select value={form.reports_to || NONE_VALUE}
                    onValueChange={v => setForm(f => ({ ...f, reports_to: v === NONE_VALUE ? '' : v }))}>
              <SelectTrigger data-testid="tm-reports-to"><SelectValue placeholder="Nobody" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>— Nobody —</SelectItem>
                {managerCandidates.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role] || u.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Phone">
            <Input value={form.phone}
                   onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                   data-testid="tm-phone" placeholder="+91…" />
          </Field>
          <Field label="Status">
            <Select value={form.status}
                    onValueChange={v => setForm(f => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="tm-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {USER_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {!isEdit && (
            <Field label="Password (optional)">
              <Input value={form.password}
                     onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                     data-testid="tm-password"
                     placeholder="Leave blank → auto-generated" />
            </Field>
          )}
        </div>

        {tempPw && (
          <div className="mt-3 p-3 rounded-md bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning)/.35)]"
               data-testid="tm-temp-password">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-[hsl(var(--warning))]">
              Temporary password (shown once)
            </div>
            <div className="flex items-center gap-2 mt-1">
              <code className="font-mono text-[13px]">{tempPw}</code>
              <button onClick={() => { navigator.clipboard.writeText(tempPw); toast.success('Copied'); }}
                      className="text-[11px] px-1.5 py-0.5 rounded hover:bg-[hsl(var(--surface-1))] flex items-center gap-1">
                <Copy size={10} /> Copy
              </button>
            </div>
            <div className="text-[11px] text-[hsl(var(--foreground-muted))] mt-1">
              Share out-of-band with the teammate. They can change it after first login.
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
          {!tempPw && (
            <Button size="sm" onClick={submit} disabled={busy} data-testid="tm-submit">
              <Save size={13} className="mr-1.5" />
              {busy ? 'Saving…' : (isEdit ? 'Save changes' : 'Create teammate')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">
        {label}{required && <span className="text-[hsl(var(--danger))] ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

function makeEmpty() {
  return { name: '', email: '', role: 'team', department: '', reports_to: '',
           phone: '', status: 'active', password: '' };
}
